<script>
import { mapGetters } from 'vuex';
import router, { routesWithPermissions } from '../dashboard/routes';
import { getUserPermissions, hasPermissions } from './helper/permissionsHelper';
import AddAccountModal from '../dashboard/components/layout/sidebarComponents/AddAccountModal.vue';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import UpdateBanner from './components/app/UpdateBanner.vue';
import ReleaseNotesModal from './components/app/ReleaseNotesModal.vue';
import UpgradeBanner from './components/app/UpgradeBanner.vue';
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
import AiCreditsExhaustedBanner from './components/app/AiCreditsExhaustedBanner.vue';
import BillingOperationalBanner from './components/app/BillingOperationalBanner.vue';
import FranchiseStatusBanner from './components/app/FranchiseStatusBanner.vue';
import AnalyticsConsentBanner from 'shared/components/AnalyticsConsentBanner.vue';
import BillingLimitModal from './components/billing/BillingLimitModal.vue';
import vueActionCable from './helper/actionCable';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import { setColorTheme } from './helper/themeHelper';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';
import ReconnectService from 'dashboard/helper/ReconnectService';
import { syncEasytableLocale } from 'dashboard/helper/easytableLocale';
import Webphone from './components/layout/webphoneComponents/Webphone.vue';

export default {
  name: 'App',

  components: {
    AddAccountModal,
    LoadingState,
    NetworkNotification,
    UpdateBanner,
    PaymentPendingBanner,
    WootSnackbarBox,
    UpgradeBanner,
    PendingEmailVerificationBanner,
    AiCreditsExhaustedBanner,
    BillingOperationalBanner,
    FranchiseStatusBanner,
    AnalyticsConsentBanner,
    BillingLimitModal,
    Webphone,
    ReleaseNotesModal,
  },
  data() {
    return {
      showAddAccountModal: false,
      latestChatwootVersion: null,
      reconnectService: null,
    };
  },

  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      isRTL: 'accounts/isRTL',
      currentUser: 'getCurrentUser',
      authUIFlags: 'getAuthUIFlags',
      accountUIFlags: 'accounts/getUIFlags',
      currentAccountId: 'getCurrentAccountId',
      billingLimitModal: 'billingEntitlements/getModal',
    }),
    hasAccounts() {
      const { accounts = [] } = this.currentUser || {};
      return accounts.length > 0;
    },
    hideOnOnboardingView() {
      return !isOnOnboardingView(this.$route);
    },
    // O aviso de novidade é a mesma informação da tela de Notas de Atualização, chegando
    // sozinha. Lê a permissão DA ROTA para não haver como as duas divergirem: quem não pode
    // abrir a tela também não recebe o aviso.
    canViewReleaseNotes() {
      if (!this.currentAccountId) return false;

      return hasPermissions(
        routesWithPermissions.release_notes_list,
        getUserPermissions(this.currentUser, this.currentAccountId)
      );
    },
  },

  watch: {
    currentUser() {
      if (!this.hasAccounts) {
        this.showAddAccountModal = true;
      }
    },
    currentAccountId() {
      if (this.currentAccountId) {
        this.initializeAccount();
      }
    },
  },
  mounted() {
    this.initializeColorTheme();
    this.listenToThemeChanges();
    this.setLocale(window.chatwootConfig.selectedLocale);
    this.$emitter.on(
      BUS_EVENTS.BILLING_LIMIT_REACHED,
      this.openBillingLimitModal
    );
    this.$emitter.on(
      BUS_EVENTS.BILLING_UPDATED,
      this.refreshBillingEntitlements
    );
  },
  beforeDestroy() {
    this.$emitter.off(
      BUS_EVENTS.BILLING_LIMIT_REACHED,
      this.openBillingLimitModal
    );
    this.$emitter.off(
      BUS_EVENTS.BILLING_UPDATED,
      this.refreshBillingEntitlements
    );
    if (this.reconnectService) {
      this.reconnectService.disconnect();
    }
  },
  methods: {
    initializeColorTheme() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches);
    },
    setLocale(locale) {
      window.chatwootConfig.selectedLocale = locale;
      this.$root.$i18n.locale = locale;
      // O `vue-easytable` tem catálogo próprio e não acompanha o do app: sem este empurrão
      // o paginador de toda tabela do produto fica em inglês.
      syncEasytableLocale(this.$root.$i18n);
    },
    async initializeAccount() {
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      this.refreshBillingEntitlements();
      const { locale, latest_chatwoot_version: latestChatwootVersion } =
        this.getAccount(this.currentAccountId);
      const { pubsub_token: pubsubToken } = this.currentUser || {};
      this.setLocale(locale);
      this.latestChatwootVersion = latestChatwootVersion;
      vueActionCable.init(pubsubToken);
      this.reconnectService = new ReconnectService(this.$store, router);

      verifyServiceWorkerExistence(registration =>
        registration.pushManager.getSubscription().then(subscription => {
          if (subscription) {
            registerSubscription();
          }
        })
      );
    },
    openBillingLimitModal(entitlement) {
      this.$store.dispatch('billingEntitlements/openLimitModal', entitlement);
    },
    closeBillingLimitModal() {
      this.$store.dispatch('billingEntitlements/closeLimitModal');
    },
    refreshBillingEntitlements() {
      if (!this.currentAccountId) return;

      this.$store.dispatch('billingEntitlements/fetch');
    },
  },
};
</script>

<template>
  <div
    v-if="!authUIFlags.isFetching && !accountUIFlags.isFetchingItem"
    id="app"
    class="flex flex-col flex-grow-0 w-full h-full min-h-0 overflow-hidden app-wrapper"
    :class="{ 'app-rtl--wrapper': isRTL }"
    :dir="isRTL ? 'rtl' : 'ltr'"
  >
    <Webphone />
    <UpdateBanner :latest-chatwoot-version="latestChatwootVersion" />
    <template v-if="currentAccountId">
      <ReleaseNotesModal v-if="hideOnOnboardingView && canViewReleaseNotes" />
      <PendingEmailVerificationBanner v-if="hideOnOnboardingView" />
      <PaymentPendingBanner v-if="hideOnOnboardingView" />
      <BillingOperationalBanner v-if="hideOnOnboardingView" />
      <FranchiseStatusBanner v-if="hideOnOnboardingView" />
      <AiCreditsExhaustedBanner v-if="hideOnOnboardingView" />
      <UpgradeBanner />
    </template>
    <div class="flex flex-1 min-h-0 overflow-hidden">
      <transition name="fade" mode="out-in">
        <router-view />
      </transition>
    </div>
    <AnalyticsConsentBanner inline />
    <AddAccountModal :show="showAddAccountModal" :has-accounts="hasAccounts" />
    <BillingLimitModal
      :show="billingLimitModal.show"
      :entitlement="billingLimitModal.entitlement"
      :account-id="currentAccountId"
      @close="closeBillingLimitModal"
    />
    <WootSnackbarBox />
    <NetworkNotification />
  </div>
  <LoadingState v-else />
</template>

<style lang="scss">
@import './assets/scss/app';
</style>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
